/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : CustomLegalStandard.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import { Box, Typography, Divider, Paper, IconButton } from "@mui/material";
import CustomSelectV2 from "../common/select/CustomSelectV2";
import EditIcon from "@mui/icons-material/Edit";
import AddButtonV2 from "../common/button/AddButtonV2";
import dayjs from "dayjs";
import { componentStyle } from "../../../config/appTheme";
import CustomToolTip from "../../infoButton/CustomToolTip";

const formatDateValue = (dateVal) => {
  if (!dateVal) return "-";
  if (/^\d{2}-\d{4}$/.test(dateVal)) return dateVal;
  return dayjs(dateVal).format("MM-YYYY");
};

function CustomLegalStandard({
  legalStandardsData = [],
  handleEditClick,
  handleOpenCloseAddLS,
  refreshLegalStandards,
  setExportSelectedLsDetails,
}) {
  const items = useMemo(
    () => legalStandardsData?.[0]?.items || [],
    [legalStandardsData],
  );

  const versionOptions = useMemo(() => {
    return [...items] // clone to avoid mutating Redux state
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at)) // descending
      .map((item) => ({
        label: `${item.version} - ${dayjs(item?.created_at).format(
          "DD MMM YYYY",
        )}`,
        value: item.version,
      }));
  }, [items]);

  const [selectedVersion, setSelectedVersion] = useState("");

  const standard = useMemo(
    () => items.find((i) => i.version === selectedVersion) || {},
    [items, selectedVersion],
  );

  useEffect(() => {
    if (items.length > 0) {
      setSelectedVersion(items[items.length - 1].version);
    }
  }, [items]);

  useEffect(() => {
    if (setExportSelectedLsDetails) {
      setExportSelectedLsDetails(standard);
    }
  }, [standard, setExportSelectedLsDetails]);

  const moduleTypeData = (data) => {
    switch (data) {
      case 2:
        return "EE Components";

      case 1:
        return "Software Modules";

      case 3:
        return "Vehicle Systems";

      case 4:
        return "Vehicle Components";

      default:
        return "-";
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 1,
        }}
      >
        <IconButton onClick={() => handleEditClick(true, standard)}>
          <EditIcon />
        </IconButton>

        <AddButtonV2 handleOnClick={() => handleOpenCloseAddLS(true)} />
      </Box>
      <Paper
        sx={{
          p: 1,
          width: "100%",
          mx: "auto",
          textAlign: "left",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2,
          }}
        >
          <Box>
            <CustomToolTip
              type="string"
              length={50}
              style={{
                ...componentStyle.font.text,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "100%",
                fontWeight: "bold",
              }}
              title={`Standard Name: ${standard.name || "N/A"}`}
            >
              {`${standard.name || "N/A"}`}
            </CustomToolTip>

            <CustomToolTip
              type="string"
              length={50}
              style={{
                ...componentStyle.font.text,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "100%",
              }}
              title={`Standard No: ${standard.standard_number || "N/A"}`}
            >
              {`Standard No: ${standard.standard_number || "N/A"}`}
            </CustomToolTip>
            <Typography sx={{ fontSize: "0.9rem", color: "text.secondary" }}>
              Category: {moduleTypeData(standard.module_type) || "software"}
            </Typography>
            <Typography sx={{ fontSize: "0.9rem", color: "text.secondary" }}>
              Status: {standard.type_of_changes || "N/A"}
            </Typography>
          </Box>

          <Box sx={{ width: "30%" }}>
            <Box sx={{ width: "100%" }}>
              <CustomSelectV2
                label="Version - Date"
                enableSearch={true}
                optionsList={versionOptions}
                selectedValue={selectedVersion}
                sltFldStyle={{
                  minHeight: "45px",
                  fontSize: "0.95rem",
                  borderRadius: 0,
                }}
                handleOnChange={(e) => setSelectedVersion(e.target.value)}
              />
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 2, display: "flex" }}>
          <Typography
            sx={{
              color: "text.secondary",
              fontSize: "0.95rem",
              mb: 0.5,
            }}
          >
            Summary:
          </Typography>
          <CustomToolTip
            type="string"
            length={50}
            style={{
              ...componentStyle.font.text,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "100%",
              mt: 0.3,
            }}
            title={`Summary: ${standard.summary || "N/A"}`}
          >
            {`${standard.summary || "N/A"}`}
          </CustomToolTip>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 2, display: "flex" }}>
          <Typography
            sx={{ fontSize: "0.95rem", color: "text.secondary", mb: 0.5 }}
          >
            Description:
          </Typography>
          <CustomToolTip
            type="string"
            length={50}
            style={{
              ...componentStyle.font.text,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "100%",
            }}
            title={`Description: ${standard.description || "N/A"}`}
          >
            {`${standard.description || "N/A"}`}
          </CustomToolTip>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 3,
            flexWrap: "wrap",
            mb: 2,
          }}
        >
          <Box sx={{ flex: 1, minWidth: "45%" }}>
            <Typography
              sx={{ fontSize: "0.95rem", mb: 0.5, color: "text.secondary" }}
            >
              Delivery Markets:
            </Typography>
            <Box sx={{ width: "100%" }}>
              <CustomSelectV2
                showLabel={false}
                required={false}
                selectedValue={standard?.markets?.[0]}
                optionsList={(standard?.markets || []).map((data) => ({
                  value: data,
                  label: data,
                }))}
                sltFldStyle={{
                  minHeight: "45px",
                  fontSize: "0.95rem",
                  borderRadius: 0,
                }}
              />
            </Box>
          </Box>

          <Box sx={{ flex: 1, minWidth: "45%" }}>
            <Typography
              sx={{
                fontSize: "0.95rem",
                mb: 0.5,
                color: "text.secondary",
              }}
            >
              Tags / Chapters:
            </Typography>

            <Box sx={{ width: "100%" }}>
              <CustomSelectV2
                showLabel={false}
                required={false}
                selectedValue={standard?.chapters?.[0]}
                optionsList={(standard?.chapters || []).map((data) => ({
                  value: data,
                  label: data,
                }))}
                sltFldStyle={{
                  minHeight: "45px",
                  fontSize: "0.95rem",
                  borderRadius: 0,
                }}
              />
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Box>
            <Typography sx={{ fontWeight: "bold", fontSize: "0.9rem" }}>
              Effective From:
            </Typography>
            <Box sx={{ mt: 0.5, borderRadius: 1, p: 1, minWidth: 120 }}>
              <Typography sx={{ fontSize: "0.9rem", color: "text.secondary" }}>
                {formatDateValue(standard?.effective_from)}
              </Typography>
            </Box>
          </Box>
          <Box>
            <Typography sx={{ fontWeight: "bold", fontSize: "0.9rem" }}>
              Validate Till:
            </Typography>
            <Box sx={{ mt: 0.5, borderRadius: 1, p: 1, minWidth: 120 }}>
              <Typography sx={{ fontSize: "0.9rem", color: "text.secondary" }}>
                {formatDateValue(standard?.valid_till)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}

CustomLegalStandard.propTypes = {
  legalStandardsData: PropTypes.array,
  handleEditClick: PropTypes.func,
  handleOpenCloseAddLS: PropTypes.func,
  refreshLegalStandards: PropTypes.func,
  setExportSelectedLsDetails: PropTypes.func,
};

export default CustomLegalStandard;
